import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import classes from "./styles.module.scss";

import { Form } from "@components/formControls/baseForm";
import type { ISendEmailFormValues } from "@components/organisms/videoVisit/components/inviteGuest/schema";
import {
  SendEmailInitialValues,
  SendEmailSchema,
} from "@components/organisms/videoVisit/components/inviteGuest/schema";
import { useSendInviteEmail } from "@api/videoVisit";
import { useNotifyPromise } from "@hooks";
import { useVideoVisitStore } from "@components/organisms/videoVisit";
import { Grid } from "@components/atoms/Grid";
import { FastInput, FastPhone, FastRadio } from "@components/fastFields";
import { Button } from "@components/atoms/button";
import { ListBox } from "@components/molecules/listBox";
import { useEmailColumns } from "@components/organisms/videoVisit/components/inviteGuest/columns";
import { notify } from "@components/atoms/notify";
import { closeAlert } from "@/stores/uiStore";
import { deepComparison, encodePhone } from "@utils";
import type { IInviteGuestProps } from "@components/organisms/videoVisit/components/inviteGuest/types";

export function SendInviteEmail({ id: appointmentId }: IInviteGuestProps) {
  const { t } = useTranslation("form");

  const videoVisitAppointmentId = useVideoVisitStore(
    (state) => state.appointmentId,
  )!;

  const id = appointmentId || videoVisitAppointmentId;

  const notifyMutate = useNotifyPromise();
  const { mutateAsync, isLoading } = useSendInviteEmail();

  const formik = useFormik<ISendEmailFormValues>({
    initialValues: SendEmailInitialValues,
    validationSchema: SendEmailSchema,
    onSubmit: () => {},
  });

  const onSubmit = () => {
    notifyMutate(
      mutateAsync({
        appointmentId: id!,
        emails: formik.values.items
          ?.filter((item) => item?.type === "email")
          ?.map((item) => item?.value),
        mobiles: formik.values.items
          ?.filter((item) => item?.type === "phone")
          ?.map((item) => item?.value),
      }),
    )
      .then(() => closeAlert())
      .catch(() => closeAlert());
  };

  const columns = useEmailColumns((item) => {
    formik.setFieldValue(
      "items",
      formik.values.items?.filter(
        (i) => i.value !== item?.value || i?.type !== item?.type,
      ),
    );
  });

  const { email, phone, isEmail, items } = formik.values;

  const addHandler = async () => {
    await formik.submitForm();
    const errs = await formik.validateForm();

    if ((isEmail && errs?.email) || (!isEmail && errs?.phone)) {
      return;
    }

    const foundIndex = items.findIndex((item) =>
      deepComparison(item, {
        type: isEmail ? "email" : "phone",
        value: email || phone,
      }),
    );

    if (foundIndex === -1) {
      const _list = [...items];

      _list.push({
        type: isEmail ? "email" : "phone",
        value: isEmail ? email : encodePhone(phone),
      });
      formik.resetForm();
      formik.setFieldValue("items", _list);
    } else {
      notify.error({
        title: t("errors.duplicateItem"),
        text: t("errors.duplicateSelectedItem", { row: foundIndex + 1 }),
      });
    }
  };

  return (
    <Form
      isLoading={isLoading}
      isLoaderLoading={false}
      isLoaderError={false}
      mappedData={{}}
      noButtons
      validateForm={formik.validateForm}
    >
      <Grid noMarginBottom>
        <Grid.FullWidthColumn>
          <div className={"d-flex gap-l1"}>
            <FastRadio
              name={"isEmail"}
              title={"phone"}
              value={false}
              formik={formik}
            />
            <FastRadio name={"isEmail"} title={"email"} value formik={formik} />
          </div>
        </Grid.FullWidthColumn>
        {formik.values.isEmail ? (
          <FastInput name={"email"} type={"email_notif"} formik={formik} />
        ) : (
          <FastPhone name={"phone"} formik={formik} />
        )}
        <Grid.Button>
          <Button mode={"add"} onClick={() => addHandler()} />
        </Grid.Button>
        <Grid.FullWidthColumn>
          <ListBox
            title={"emailsAndPhones"}
            columnDef={columns}
            dataJSON={formik?.values?.items?.map((i) => ({
              email: i.value,
              phone: i.value,
              isEmail: i.type === "email",
            }))}
          />
        </Grid.FullWidthColumn>
        <Grid.FullWidthColumn>
          <div className={clsx(classes.buttons)}>
            <Button
              mode={"secondary"}
              label={t("close")}
              onClick={() => closeAlert()}
            />
            {items.length > 0 && (
              <Button
                mode={"main"}
                label={t("send")}
                onClick={() => onSubmit()}
                isLoading={isLoading}
              />
            )}
          </div>
        </Grid.FullWidthColumn>
      </Grid>
    </Form>
  );
}
